import { useState, type ChangeEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  List, ListItemButton, ListItemText, Stack, TextField, Typography,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useAnalyticsApi } from '@/lib/useAnalytics';
import { useAuth } from '@/lib/auth/AuthContext';
import type { ZnalezionaRestauracja } from '@/lib/api/analytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PODGLĄD RESTAURACJI — wejście właściciela w panel konkretnej restauracji.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ TO NIE JEST LOGOWANIE JAKO RESTAURACJA ────────────────────────────────────────────────────┐
 * │ Właściciel NIE podaje hasła restauracji — nie zna go i nie musi. Podaje WŁASNE hasło,       │
 * │ potwierdzając, że wolno mu wejść w cudze dane. Serwer wydaje wtedy krótką sesję ograniczoną │
 * │ do jednej restauracji.                                                                       │
 * │                                                                                              │
 * │ Hasło żyje przez czas jednego żądania: nie trafia do stanu komponentu na dłużej, do pamięci │
 * │ przeglądarki, do adresu ani do dziennika.                                                    │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 */

/** Odświeżamy rzadko — stan podglądu zmienia się tylko wtedy, gdy właściciel sam go zmieni. */
function useStanPodgladu() {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['podglad-stan'],
    queryFn: () => api.stanPodgladu(),
    staleTime: 60_000,
    retry: false,
  });
}

function OknoWejscia({ otwarte, onZamknij }: { otwarte: boolean; onZamknij: () => void }) {
  const api = useAnalyticsApi();
  const kolejka = useQueryClient();

  const [fraza, setFraza] = useState('');
  const [wyniki, setWyniki] = useState<ZnalezionaRestauracja[] | null>(null);
  const [wybrana, setWybrana] = useState<ZnalezionaRestauracja | null>(null);
  const [haslo, setHaslo] = useState('');
  const [blad, setBlad] = useState<string | null>(null);
  const [pracuje, setPracuje] = useState(false);

  async function szukaj() {
    setBlad(null);
    setPracuje(true);
    try {
      const w = await api.szukajRestauracji(fraza.trim());
      setWyniki(w.restauracje);
    } catch (e) {
      setWyniki(null);
      setBlad(e instanceof Error ? e.message : 'Nie udało się wyszukać.');
    } finally {
      setPracuje(false);
    }
  }

  async function wejdz() {
    if (!wybrana) return;
    setBlad(null);
    setPracuje(true);
    try {
      await api.wejdzWPodglad(wybrana.restauracja_id, haslo);
      /*
       * Hasło znika z pamięci komponentu NATYCHMIAST po wysłaniu — nie czekamy na zamknięcie
       * okna. Stan Reacta żyje tyle, co karta przeglądarki; hasło ma żyć tyle, co żądanie.
       */
      setHaslo('');
      await kolejka.invalidateQueries();
      onZamknij();
    } catch (e) {
      setHaslo('');
      setBlad(e instanceof Error ? e.message : 'Nie udało się wejść w podgląd.');
    } finally {
      setPracuje(false);
    }
  }

  return (
    <Dialog open={otwarte} onClose={onZamknij} fullWidth maxWidth="sm">
      <DialogTitle>Podgląd restauracji</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            Wchodzisz w dane wybranej restauracji <strong>swoim</strong> hasłem. Hasła restauracji
            nie znamy i nie jest ono do niczego potrzebne.
          </Alert>

          <Stack direction="row" spacing={1}>
            <TextField
              label="Login restauracji" size="small" fullWidth value={fraza}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFraza(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void szukaj(); }}
              helperText="Co najmniej trzy znaki"
            />
            <Button onClick={() => void szukaj()} disabled={pracuje || fraza.trim().length < 3}>
              Szukaj
            </Button>
          </Stack>

          {wyniki && wyniki.length === 0 && (
            <Alert severity="warning">Nie znaleziono restauracji o takim loginie.</Alert>
          )}

          {wyniki && wyniki.length > 0 && (
            <List dense sx={{ maxHeight: 220, overflow: 'auto' }}>
              {wyniki.map((r) => (
                <ListItemButton
                  key={r.restauracja_id}
                  selected={wybrana?.restauracja_id === r.restauracja_id}
                  onClick={() => setWybrana(r)}
                >
                  {/* Nazwa i miasto obok loginu — pomyłka tutaj znaczy oglądanie cudzych danych. */}
                  <ListItemText
                    primary={r.nazwa}
                    secondary={`${r.login}${r.miasto ? ` · ${r.miasto}` : ''}`}
                  />
                </ListItemButton>
              ))}
            </List>
          )}

          {wybrana && (
            <TextField
              label="Twoje hasło" type="password" size="small" value={haslo}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setHaslo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && haslo) void wejdz(); }}
              autoComplete="current-password"
              helperText="Potwierdzenie, że wolno Ci wejść w te dane. Nigdzie nie jest zapisywane."
            />
          )}

          {blad && <Alert severity="error">{blad}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onZamknij}>Anuluj</Button>
        <Button variant="contained" disabled={!wybrana || !haslo || pracuje} onClick={() => void wejdz()}>
          Wejdź w podgląd
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Baner i wejście. Widoczny dla właściciela na każdym ekranie, bo stoi w powłoce aplikacji.
 *
 * BANER JEST STAŁY I NIE DA SIĘ GO PRZEOCZYĆ. Właściciel nigdy nie może się zastanawiać,
 * czyje dane właśnie ogląda — pomyłka tutaj to decyzja podjęta na cudzych liczbach.
 */
export function PodgladRestauracji() {
  const auth = useAuth();
  const api = useAnalyticsApi();
  const kolejka = useQueryClient();
  const [okno, setOkno] = useState(false);

  // Tylko właściciel. Dla pozostałych ról ten element nie istnieje — a i tak serwer odmówiłby.
  const czyWlasciciel = auth.has('admin.users.read');
  const stan = useStanPodgladu();

  if (!czyWlasciciel) return null;

  const aktywny = stan.data?.aktywny === true;

  async function zakoncz() {
    await api.zakonczPodglad();
    await kolejka.invalidateQueries();
  }

  return (
    <>
      {aktywny ? (
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
            px: 1.5, py: 0.5, borderRadius: 1,
            background: 'var(--uwaga-tlo)', border: '1px solid var(--uwaga)',
          }}
        >
          <StorefrontIcon fontSize="small" sx={{ color: 'var(--uwaga)' }} />
          <Typography variant="caption" sx={{ color: 'var(--uwaga)', fontWeight: 700 }}>
            PODGLĄD RESTAURACJI:
          </Typography>
          <Chip size="small" label={stan.data?.nazwa ?? `nr ${stan.data?.restauracja_id}`} />
          <Button size="small" onClick={() => setOkno(true)}>Zmień</Button>
          <Button size="small" color="inherit" onClick={() => { void zakoncz(); }}>
            Zakończ podgląd
          </Button>
        </Box>
      ) : (
        <Button
          size="small"
          color="inherit"
          startIcon={<StorefrontIcon />}
          onClick={() => setOkno(true)}
          sx={{ flex: 'none' }}
        >
          Podgląd restauracji
        </Button>
      )}

      {okno && <OknoWejscia otwarte={okno} onZamknij={() => setOkno(false)} />}
    </>
  );
}
